import { useSyncExternalStore } from 'react';
import { getApiOrigin } from '../config/api';

/**
 * ¿Llega este dispositivo al servidor?
 *
 * No se usa `navigator.onLine`: dice si hay red local, no si hay internet ni si el
 * servidor responde, y en una tablet conectada a un módem sin salida dice "en línea"
 * todo el día. La fuente de verdad son las peticiones reales: cada una que llega al
 * servidor marca "en línea" y cada caída de red, timeout o 502-504 (Render caído o
 * redeployando) marca "sin conexión". El evento `offline` del navegador sí se usa,
 * porque cuando dice que NO hay red es confiable.
 *
 * Mientras está sin conexión se sondea /health para quitar el aviso aunque la
 * pantalla no haga peticiones propias (el mapa de mesas sin nadie tocándolo).
 */
export type ConnectionStatus = 'online' | 'offline';

const PROBE_INTERVAL_MS = 10_000;
const PROBE_TIMEOUT_MS = 5_000;

let status: ConnectionStatus = 'online';
let probeTimer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

const setStatus = (next: ConnectionStatus) => {
  if (next === status) return;
  status = next;

  if (next === 'offline' && !probeTimer) {
    probeTimer = setInterval(probe, PROBE_INTERVAL_MS);
  } else if (next === 'online' && probeTimer) {
    clearInterval(probeTimer);
    probeTimer = null;
  }

  listeners.forEach((listener) => listener());
};

async function probe() {
  // AbortController a mano y no AbortSignal.timeout: las tablets viejas de piso
  // traen navegadores que no lo tienen.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const response = await fetch(`${getApiOrigin()}/health`, {
      cache: 'no-store',
      signal: controller.signal,
    });
    if (response.ok) setStatus('online');
  } catch {
    // Sigue sin conexión; el siguiente sondeo lo vuelve a intentar.
  } finally {
    clearTimeout(timer);
  }
}

/** Códigos que devuelve el proxy de Render cuando el servicio no está contestando. */
const GATEWAY_STATUSES = new Set([502, 503, 504]);

/**
 * ¿Este error de RTK Query significa que no se llegó al servidor? Un 4xx o un 500
 * propio NO: el servidor contestó, solo que con un error.
 *
 * Dos trampas con 502-504:
 * - El proxy de Render contesta con una página HTML. fetchBaseQuery no la puede leer
 *   como JSON y la entrega como `PARSING_ERROR` con el estado real en `originalStatus`.
 * - La API misma responde 503/504 con cuerpo JSON (`message`) cuando falla el PAC de
 *   facturación. Eso es el servidor contestando, no una caída de conexión.
 */
export const isUnreachableError = (
  error: { status: unknown; originalStatus?: unknown; data?: unknown } | undefined
): boolean => {
  if (!error) return false;
  const code = error.status;
  if (code === 'FETCH_ERROR' || code === 'TIMEOUT_ERROR') return true;
  if (code === 'PARSING_ERROR') {
    return typeof error.originalStatus === 'number' && GATEWAY_STATUSES.has(error.originalStatus);
  }
  if (typeof code === 'number' && GATEWAY_STATUSES.has(code)) {
    const data = error.data as { message?: unknown } | null | undefined;
    return !(data && typeof data === 'object' && typeof data.message === 'string');
  }
  return false;
};

/** Lo llama baseApi después de cada petición. */
export const reportRequestOutcome = (
  error: { status: unknown; originalStatus?: unknown; data?: unknown } | undefined
) => {
  setStatus(isUnreachableError(error) ? 'offline' : 'online');
};

if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => setStatus('offline'));
  // `online` no basta para declarar que hay servidor; se confirma sondeando.
  window.addEventListener('online', () => { if (status === 'offline') probe(); });
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

export const getConnectionStatus = (): ConnectionStatus => status;

export const useConnectionStatus = (): ConnectionStatus =>
  useSyncExternalStore(subscribe, getConnectionStatus);
